import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { firstValueFrom } from 'rxjs';

import { BookingAddOnConversationDto, BookingAddOnLineDto, BookingAddOnPaymentDto } from '@hqo/resource-booking-client';

import {
  BookingAddOnsRead,
  REPRICING_DISPOSITION,
  paymentIdentity,
  WORK_ORDER_PAYMENT_STATE,
  hasPaymentBlock,
  isSettledPaymentState,
} from 'store/api-client/add-on-payment-types';
import { ResourceBookingServiceClient } from 'store/api-client/resource-booking-service-client';
import { RootState } from 'store/reducer';

/**
 * The ordered card's whole answer: the money block, and whether this order can still be changed.
 * Both come off one GET; a second hook would mean two answers to the same URL that can disagree.
 */
export interface OrderedAddOnsRead {
  /** Null when work-orders was unreachable or the tenant lacks `CAN_MANAGE_TENANT_WORK_ORDERS`. */
  payment: BookingAddOnPaymentDto | null;
  canOrderAddOns: boolean;
  cannotOrderReason: BookingAddOnsRead['cannotOrderReason'];
  /** Read off the same vendor entry as the payment, so it cannot describe a vendor the money does not. */
  amendRefusedReason: string | null;
  /*
   * RES-1387 — the tenant's thread with this vendor, and the unread count on it.
   *
   * ⚠️ NULL IS "NO THREAD TO OFFER", never "an empty thread". The service answers null on a link
   * with no work order yet and on a work-orders read that failed, so a conversation row drawn from
   * a null here would open onto nothing. Carried on this read rather than asked for separately
   * because it comes off the same vendor entry as the money — see this file's own rule above.
   */
  conversation: BookingAddOnConversationDto | null;
  /*
   * The vendor's own lines, as work-orders holds them.
   *
   * RES-1317 carried a `priceKnownByCatalogItem` map for the same purpose; this supersedes it rather
   * than sitting beside it, because the card also needs the vendor's price, quantity and
   * `confirmedWithVendor` from the same row, and two answers to "what did the vendor say about this
   * line" is how the pair drifts.
   *
   * Read `priceKnown` UNGATED, and do not reintroduce a `confirmedWithVendor` check in front of it.
   * An earlier version of this branch did, reasoning that helix marks every zero unpriced and a free
   * product would read "Price on request". The reasoning was sound and the conclusion was not: the
   * service leaves every line unconfirmed until the work order has items, which is the window right
   * after an order, so the gate withheld the service's correct answer and fell back to helix's zero
   * on exactly the quote-required rows it was meant to protect. RES-1317 #1980 reached the same
   * conclusion independently. The free-product case is the accepted cost; RES-1317 measured it at
   * zero occurrences over seven days, a figure recorded here as they reported it rather than one
   * re-measured for this change.
   *
   * `confirmedWithVendor` is still the right gate for PRICE, QUANTITY and CURRENCY, which choose
   * between two different figures and must know which side the figure came from.
   */
  lines: BookingAddOnLineDto[];
}

/* What the card is told when the read could not be made at all. The order gate fails closed: a 403
 * means we were not told whether this order may change, not that we were told yes. */
const NOTHING_ORDERED: OrderedAddOnsRead = {
  payment: null,
  canOrderAddOns: false,
  cannotOrderReason: null,
  amendRefusedReason: null,
  conversation: null,
  lines: [],
};

/** How often to re-ask while a charge is on its way into the work order. */
const SETTLE_POLL_MS = 2_000;

/*
 * ⛔ HOW LONG A CHARGE IS GIVEN TO REACH THE RECORD, and it is measured rather than guessed: on
 * SR-DM4XHP5 the session was opened at 04:53:35 and `paymentState` flipped to `paid` at 04:53:46.
 * Eleven seconds is the shape of it, so fifteen is the cap -- the same figure the add-on step's own
 * hook uses, and deliberately the same, because it is the same backend hop being waited on.
 */
const SETTLE_GIVE_UP_MS = 15_000;

/*
 * SETTLE_GIVE_UP_MS measures one hop; this measures the whole create chain — the selection is
 * written, CDC carries it, a consumer creates the linked service request, billing opens a session.
 * Same figure as `PENDING_GIVE_UP_MS` in `useAddOnPaymentQuery`, measured against this chain.
 */
export const CREATE_GIVE_UP_MS = 45_000;

/*
 * Has the record caught up with a cancel? `isSettledPaymentState` is wrong after one: a tenant who
 * paid and then cancels sits on `paid`, which it already calls settled, so the poll would stop
 * before the refund lands one hop later. A never-paid order settles here on the first answer.
 */
const hasRefundLanded = (paymentState: string | null | undefined): boolean =>
  paymentState !== WORK_ORDER_PAYMENT_STATE.Paid;

/*
 * RES-1316 — the payment block for add-ons ALREADY ordered, read once and then left alone, except
 * for the one window in which a charge the tenant just made is on its way into the record.
 *
 * ⛔ A SECOND HOOK ONTO THE SAME ENDPOINT, DELIBERATELY, AND IT IS NOT DUPLICATION. The question is
 * different, so the machinery has to be. `useAddOnPaymentQuery` asks "will a checkout session arrive
 * for the order I just placed", and most of what it carries exists to answer that while it is still
 * in flight: a 2s poll on an absent session, a 45s give-up deadline, and a ref that stops any later
 * answer revoking a session mid-payment. All of that is wrong here. Nothing is in flight on a booking
 * from last Tuesday; the order is finished and the work order says what happened to it. Pointing the
 * live hook at this surface would poll a settled record, and its `isExpected` gate would have to be
 * lied to for it to ask at all.
 *
 * ⭐ ONE PIECE OF IT DOES BELONG HERE, AND LEAVING IT OUT WAS A BUG. The moment this surface lets the
 * tenant PAY (RES-1367's pay-later control), it inherits the settle race the add-on step was already
 * solving: billing reports success the instant the gateway takes the money, work-orders records it
 * about eleven seconds later, and a single re-read on close lands inside that gap. The block then
 * says "awaiting payment" on an order that is paid, and stays wrong until the tenant leaves the
 * booking and comes back -- which is exactly how it was reported. A read-once hook was right only
 * while this surface was read-only. See `chargedAt` and `dismissedAt` below.
 *
 * ⚠️ ITS OWN QUERY KEY, for the same reason. The two never mount together — the live one belongs to
 * the add-on step, this one to the booking detail — but they return DIFFERENT SHAPES from the same
 * URL, so sharing a key would let whichever ran first serve the other a cached answer it cannot read.
 *
 * ⚠️ FAILURE IS "NO PAYMENT DETAILS", NEVER AN ERROR. The read sits behind
 * `CAN_MANAGE_TENANT_WORK_ORDERS` and a tenant without that role at the building gets a 403; the
 * service also answers a null `payment` when work-orders cannot be reached. The add-ons were still
 * ordered and helix still has the rows, so the section renders them with no money block rather than
 * failing — which is the same additive-section rule helix applies on its own side.
 *
 * ⚠️ ONE VENDOR. RES-1051 enforces a single party per booking, so this list is empty or holds one
 * entry. `.find` rather than `[0]` because a second vendor should pick the one with a work order.
 */
export const useOrderedAddOnPaymentQuery = (
  buildingUuid: string | null,
  bookingId: number | null,
  /*
   * The section is showing. Gated on the caller's own visibility decision rather than re-derived,
   * so a building with the feature off never asks — and so this cannot ask on a booking that has no
   * vendor rows to ask about.
   */
  isVisible: boolean,
  /*
   * RES-1367 — billing reported a successful charge, and WHEN. It is the only reliable signal that
   * money moved: the read below cannot tell "not paid yet" from "paid, not recorded yet", because
   * both answer `pending_payment`.
   *
   * ⚠️ A TIMESTAMP, NOT A FLAG, and that is what makes a second payment work. The window below is
   * measured from this value, so each new charge restarts it; a boolean would arm the window once per
   * mount and leave a tenant who pays, is declined, and pays again watching a window that expired
   * during their first attempt.
   */
  chargedAt: number | null = null,
  /*
   * RES-1367 — the tenant LEFT billing's sheet, however it ended, and when. A second and WEAKER
   * reason to watch, and it exists because `chargedAt` can simply be lost: the widget reports success
   * and then renders its own screen, so anything that breaks in between — a throw after the gateway
   * already took the money — leaves a paid order this app never hears about.
   *
   * ⛔ IT ARMS THE WINDOW ONLY — NEVER THE FOCUS REFETCH — and that asymmetry is the whole of it. A
   * reported charge earns asking until the record catches up. A dismissal only means a sheet was
   * closed, and most are abandoned, declined or expired; it earns one bounded 15s look and nothing
   * after it. Arming focus from here would re-read this block on every tab focus for the life of
   * every booking whose add-ons were never paid for.
   */
  dismissedAt: number | null = null,
  /*
   * The service accepted a cancel, and when; a timestamp so each cancel starts a fresh window. It
   * watches for a different ending than the two above — see `hasRefundLanded`.
   */
  cancelledAt: number | null = null,
  /*
   * An order was placed against this booking, and when. Not one more settle signal: it waits for the
   * record to exist at all, so it has its own stop condition and longer deadline below. A parameter
   * rather than this card's own state, because the add-on dialog's route is a sibling of the
   * receipt's and the card is unmounted for the whole ordering flow (see `selectAddOnsOrderedAt`).
   * Arms the window only, never the focus refetch: a create that failed never produces a block.
   */
  orderedAt: number | null = null,
  /*
   * RES-1317 -- the money as it stood just before that order was submitted. The poll below runs until
   * the server stops answering with it; null disarms that clause rather than pinning it to a value
   * nothing can match. The card tests the same value again at render, where it withholds.
   */
  orderedPaymentKey: string | null = null,
) => {
  const apiUrl = useSelector((rootState: RootState) => rootState.config.apiUrl);
  const authToken = useSelector((rootState: RootState) => rootState.config.authToken);

  /* Whichever signal arrived last starts the clock; null when none has. */
  const watchingSince = Math.max(chargedAt ?? 0, dismissedAt ?? 0, cancelledAt ?? 0) || null;

  const isWithinSettleWindow = (): boolean => watchingSince !== null && Date.now() - watchingSince < SETTLE_GIVE_UP_MS;

  const isWithinCreateWindow = (): boolean => orderedAt !== null && Date.now() - orderedAt < CREATE_GIVE_UP_MS;

  /*
   * No block yet, or one work-orders is still pricing. An amend keeps its block and re-prices in
   * place, so waiting for a block to appear would stop on the first poll and leave the superseded
   * figure on screen until a manual refresh; the disposition is what separates the two.
   */
  const isAwaitingPrice = (payment: BookingAddOnPaymentDto | null | undefined): boolean =>
    !payment || payment.paymentDisposition === REPRICING_DISPOSITION;

  /*
   * Still answering with the money from before the submit. Production returned `priced` here, and
   * `settled` on a paid order, so `isAwaitingPrice` alone is false on the first tick and the poll
   * gives up while the figure on screen is still the superseded one.
   */
  const isServerBehind = (payment: BookingAddOnPaymentDto | null | undefined): boolean =>
    orderedPaymentKey !== null && paymentIdentity(payment) === orderedPaymentKey;

  /* A pay and a cancel inside one window wait on opposite movements, so the latest signal decides. */
  const hasRecordCaughtUp = (paymentState: string | null | undefined): boolean =>
    cancelledAt !== null && cancelledAt >= (chargedAt ?? 0)
      ? hasRefundLanded(paymentState)
      : isSettledPaymentState(paymentState);

  return useQuery<OrderedAddOnsRead>({
    queryKey: ['ordered-add-on-payment', buildingUuid, bookingId],
    enabled: Boolean(buildingUuid) && Boolean(bookingId) && isVisible,
    /*
     * ⛔ POLLS ONLY INSIDE THE WINDOW A CHARGE OPENED, and is `false` at every other moment — on
     * first read, on a booking nobody is paying for, and the instant the state settles. A settled
     * order is a finished record; there is nothing left for a poll to learn.
     *
     * ⚠️ `isSettledPaymentState`, not `chargeFrom`: the question is whether the record stopped
     * moving, not whether it can be rendered. An order marked paid whose amount never arrived would
     * poll out its whole window against the stricter test, for an answer that is not coming.
     *
     * ⚠️ A POLL IS THE WRONG SHAPE FOR THIS AND IT IS NOT YET AVOIDABLE — the same wall the add-on
     * step hit. work-orders records the settlement to the WORK ORDER's activity log, not to the Kafka
     * topic search-service consumes; that consumer handles `WORK_ORDER_CREATED` and
     * `WORK_ORDER_STATUS_UPDATED` only, and a payment changes `payment_state`, never `status_id`.
     * So this app's SSE reader never fires for a payment. Being told instead of asking means
     * work-orders emitting a payment event and search-service indexing it: two other services.
     */
    refetchInterval: query => {
      /*
       * The create window answers a different question: is work-orders still deciding what this
       * order costs. `hasRecordCaughtUp` is unusable — with no block there is no state, and no
       * state reads as settled. It tests `.payment`, not `.data`, because the answer object is
       * always present, and stays armed by an actual order because a null payment is also a
       * swallowed 403.
       */
      const ordered = query.state.data?.payment;

      /* Safe here but not in the fetch: `refetchInterval` is re-read after every render. */
      if (isWithinCreateWindow() && (isServerBehind(ordered) || isAwaitingPrice(ordered))) {
        return SETTLE_POLL_MS;
      }

      return isWithinSettleWindow() && !hasRecordCaughtUp(query.state.data?.payment?.paymentState)
        ? SETTLE_POLL_MS
        : false;
    },
    /*
     * ⛔ ON FOCUS ONLY BETWEEN A REPORTED CHARGE AND ITS RECORD — unbounded in time, but bounded by a
     * fact: `chargedAt` is set only once billing has said money moved, and it stops the moment the
     * work order agrees. A tenant whose charge lands after the 15s window gets the right answer when
     * they come back to the tab, which is otherwise the case that stays wrong until they leave the
     * booking entirely.
     */
    refetchOnWindowFocus: query =>
      (chargedAt !== null || cancelledAt !== null) && !hasRecordCaughtUp(query.state.data?.payment?.paymentState),
    /* ⛔ Never retry. A failure here means "no payment details", and the section renders without them. */
    retry: false,
    queryFn: async (): Promise<OrderedAddOnsRead> => {
      try {
        const client = new ResourceBookingServiceClient(apiUrl, authToken);
        const read = await firstValueFrom(client.getBookingAddOns(buildingUuid as string, bookingId as number));
        const { vendors } = read;

        /*
         * A link carries money only once it carries a work order: PENDING means the create never
         * completed and FAILED that it failed outright. CANCELLED counts — the service keeps
         * answering with the payment block on a cancelled link, and that block is the only place the
         * tenant sees the refund.
         */
        const vendor = vendors.find(entry => hasPaymentBlock(entry.status));

        return {
          payment: vendor?.payment ?? null,
          canOrderAddOns: read.canOrderAddOns,
          cannotOrderReason: read.cannotOrderReason,
          /* `?? null`, not `'unknown'`: an absent vendor is no refusal, not a refusal we cannot name. */
          amendRefusedReason: vendor?.amendRefusedReason ?? null,
          /*
           * The same vendor entry the money came off. `hasPaymentBlock` is the right gate for the
           * thread too: a link with no work order has no Service Request, and a Service Request is
           * where the conversation lives.
           */
          conversation: vendor?.conversation ?? null,
          /* Same vendor entry again, so the lines cannot describe a vendor the money does not. */
          lines: vendor?.addOns ?? [],
        };
      } catch {
        return NOTHING_ORDERED;
      }
    },
  });
};
